import { useMutation, useQueryClient } from "react-query";
import { saveClinicalProgramTargets, resetClinicalProgramTargets } from "api/clinicalManagement";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_CLINICAL_PROGRAM_TARGETS } from "@constants/reactQueryKeys";

const useSaveClinicalProgramTargets = (options: Record<string, any> = {}) => {
  const { onError = () => {}, onSuccess = () => {}, ...otherOptions } = options;
  const { showToast } = useGlobalToast();
  const queryClient = useQueryClient();

  const { mutateAsync: save, isLoading: isSaving } = useMutation(
    ({ clientId, targetIds }: { clientId: number; targetIds: number[] }) =>
      saveClinicalProgramTargets(clientId, { target_ids: targetIds }),
    {
      onSuccess: (_data, variables, context) => {
        queryClient.invalidateQueries([GET_CLINICAL_PROGRAM_TARGETS, variables.clientId]);
        onSuccess(_data, variables, context);
      },
      onError: (error, variables, context) => {
        showToast({
          message: "Whoops! Something went wrong while saving clinical program targets.",
          errorState: true,
        });
        onError(error, variables, context);
      },
      ...otherOptions,
    }
  );

  const { mutateAsync: reset, isLoading: isResetting } = useMutation(
    ({ clientId }: { clientId: number }) => resetClinicalProgramTargets(clientId),
    {
      onSuccess: (_data, variables, context) => {
        queryClient.invalidateQueries([GET_CLINICAL_PROGRAM_TARGETS, variables.clientId]);
        onSuccess(_data, variables, context);
      },
      onError: (error, variables, context) => {
        showToast({
          message: "Whoops! Something went wrong while resetting clinical program targets.",
          errorState: true,
        });
        onError(error, variables, context);
      },
      ...otherOptions,
    }
  );

  return {
    saveClinicalProgramTargets: save,
    resetClinicalProgramTargets: reset,
    isSaving,
    isResetting,
  };
};

export default useSaveClinicalProgramTargets;
